import React, { useEffect, useState } from 'react';
import { PhoneOutlined, WhatsAppOutlined } from '@ant-design/icons';
import { NdoeImage } from '../../components/ndoe';
import { buildWaLink } from '../../utils/whatsapp';
import WizardStepper from './WizardStepper';
import './deliveryWizard.css';

// Libellés des étapes pour le stepper nommé (le flux réel est dynamique).
const STEP_LABELS = { prise: 'Prise', resultat: 'Résultat', paiement: 'Paiement', recap: 'Récap' };

// Les 5 issues (mêmes valeurs serveur) rendues en GROS boutons plutôt qu'un
// menu déroulant : impossible de se tromper de ligne.
const OUTCOME_BTN = [
  { value: 'LIVREE', titre: 'Gardée', sous: 'elle l’achète', ic: '✅', tone: 'ok' },
  { value: 'RETOUR_LIVRAISON', titre: 'Rendue', sous: 'revient au magasin', ic: '↩️', tone: 'ret' },
  { value: 'EN_ESSAI', titre: 'Laissée à l’essai', sous: 'choisira plus tard', ic: '🔵', tone: 'essai' },
  { value: 'REPORTEE', titre: 'Reportée', sous: 'à représenter', ic: '🟡', tone: 'report' },
  { value: 'RETOUR_POUR_ECHANGE', titre: 'Échange', sous: 'contre une autre', ic: '🟠', tone: 'ech' },
];

// Motif de non-prise (paire décochée à la prise en charge) — mêmes valeurs que
// l'ancien écran, pour préserver la granularité de reporting côté magasin.
const REASONS = [
  ['INDISPONIBLE_AU_DEPART', 'Indisponible au départ'],
  ['PAIRE_NON_PREPAREE', 'Paire non préparée'],
  ['ERREUR_DE_PREPARATION', 'Erreur de préparation'],
  ['AUTRE', 'Autre motif'],
];

const localDateTime = () => {
  const d = new Date();
  const p = (v) => String(v).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

/**
 * Wizard de livraison (demande proprio 21/07) — remplace la saisie « à plat ».
 * Réutilise l'ÉTAT et les HANDLERS du hub (aucune logique d'argent dupliquée) :
 * la refonte est purement la FAÇON de saisir, pas le moteur.
 *
 * Étapes : prise en charge (si besoin) → résultat (une paire à la fois dès 2+,
 * écran unique si 1) → encaissement (si vente) → récap → valider.
 */
export default function DeliveryWizard({
  order, patchLine, pendingLines, activeLines, setAll,
  payment, changePayment, paymentCap, totalDue, deliveredLines, trialLines, acompteEssai, balanceDue,
  onTakeCharge, onSubmitResult, saving, onClose, onClearDraft,
  money, normalizePhoneCd, draftRestored, scope,
  error, clearError, effectiveAt, setEffectiveAt, minEffectiveAt, comment, setComment, acompteDisponible,
}) {
  // Prise-vs-résultat : fidèle à l'ancien écran (piloté par l'onglet). On montre
  // « prise » s'il reste des paires à prendre ET qu'on n'est pas dans l'onglet
  // résultats (« active ») — sauf s'il n'y a plus aucune paire en circulation.
  const enPrise = pendingLines.length > 0 && (scope !== 'active' || activeLines.length === 0);
  const [etape, setEtape] = useState(enPrise ? 'prise' : 'resultat');
  const [idx, setIdx] = useState(0);                 // paire courante (résultat séquentiel)
  const [showDetails, setShowDetails] = useState(false);
  // Modèle « essai à domicile » : le livreur présente plusieurs paires, la cliente
  // en garde peu. La VUE LISTE (défaut dès 2 paires) permet de tout marquer d'un
  // coup au lieu d'un écran par paire ; « une par une » reste dispo au besoin.
  const [vueListe, setVueListe] = useState(true);
  useEffect(() => { setEtape(enPrise ? 'prise' : 'resultat'); setIdx(0); }, [enPrise, order.id_commande]);
  // Une erreur (échec d'envoi) s'affiche dans le CTA de l'étape courante ; on la
  // lève dès qu'on change d'étape pour qu'elle ne « colle » pas aux écrans
  // suivants pendant que le livreur corrige. (Volontairement sur [etape] seul —
  // inclure `error` la ré-effacerait à l'instant où elle est posée.)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (error) clearError?.(); }, [etape]);

  const multi = activeLines.length >= 2;
  const decidees = activeLines.filter((l) => l.outcome);
  // On peut valider dès qu'AU MOINS une paire a une issue : les paires laissées
  // « à décider » restent simplement en circulation (le serveur ne reçoit que
  // les décidées, cf. submitOutcome). Cela évite de forcer un statut faux
  // (REPORTEE) juste pour pouvoir avancer.
  const auMoinsUne = decidees.length > 0;
  const nonDecidees = activeLines.filter((l) => !l.outcome);
  const aEncaisser = deliveredLines.length > 0 || trialLines.length > 0;
  const acompteCouvertVente = Math.min(Math.max(0, acompteDisponible || 0), totalDue);
  const resteAEncaisser = Math.max(0, totalDue - (acompteDisponible || 0));

  // Fil d'étapes réel de CETTE livraison. « Prise » reste dans le stepper une fois
  // franchie (sinon, avec un stepper NOMMÉ/numéroté, elle disparaîtrait et ferait
  // rétrécir le total en « Étape 1 sur 2 » juste après l'avoir terminée). Une
  // livraison du scope « à prendre » a toujours une prise → on la garde affichée
  // (elle passe en « franchie ✓ » dès qu'on est à l'étape suivante).
  const flow = [];
  if (etape === 'prise' || scope === 'take-charge') flow.push('prise');
  flow.push('resultat');
  if (aEncaisser) flow.push('paiement');
  flow.push('recap');

  // Message WhatsApp PRÉ-REMPLI : le livreur n'a plus qu'à compléter et envoyer
  // (aucun envoi automatique — il appuie lui-même sur Envoyer dans WhatsApp).
  const waTexte = `Bonjour Madame ${(order.client?.nom || '').trim()}, je suis votre livreur Ndoé Boutique.`
    .replace(' ,', ',');

  const Entete = (
    <>
      <section className="dw-client">
        <span className="dw-eyebrow">Commande #{order.id_commande}</span>
        <h2 className="dw-name">{order.client?.nom}</h2>
        {order.client?.adresse && <p className="dw-addr">📍 {order.client.adresse}</p>}
        {order.client?.telephone && (
          <div className="dw-contact">
            <a className="dw-call" href={`tel:+${normalizePhoneCd(order.client.telephone)}`}><PhoneOutlined /> Appeler</a>
            <a className="dw-wa" href={buildWaLink(order.client.telephone, waTexte)} target="_blank" rel="noopener noreferrer"><WhatsAppOutlined /> WhatsApp</a>
          </div>
        )}
      </section>
      {draftRestored && (
        <p className="dw-draft" role="status">
          <span>↩︎ Saisie précédente restaurée — vérifie avant de valider.</span>
          {onClearDraft && <button type="button" className="dw-draft-clear" onClick={onClearDraft}>Repartir de zéro</button>}
        </p>
      )}
    </>
  );

  // Étapes NOMMÉES du stepper, dérivées du flux réel (dynamique).
  const steps = flow.map((k) => ({ key: k, label: STEP_LABELS[k] }));

  // Bandeau d'erreur ancré au CTA (sticky) : toujours visible, jamais hors écran.
  const ErrCta = () => (error ? <p className="dw-error" role="alert">{error}</p> : null);

  // ── Étape prise en charge ──────────────────────────────────────────────────
  if (etape === 'prise') {
    return (
      <main className="dw">
        <button type="button" className="dw-back" onClick={onClose}>← Mes livraisons</button>
        {Entete}
        <div className="dw-step">
          <WizardStepper steps={steps} current={etape} />
          <h3 className="dw-q">Quelles paires as-tu prises&nbsp;?</h3>
          <div className="dw-bulk">
            <button type="button" onClick={() => setAll(true)}>Tout prendre</button>
            <button type="button" className="ghost" onClick={() => setAll(false)}>Tout retirer</button>
          </div>
          <div className="dw-list">
            {pendingLines.map((l) => (
              <div key={l.id_detail_livraison} className={`dw-pairwrap${l.taken ? '' : ' is-off'}`}>
                <button type="button"
                  className={`dw-pair${l.taken ? ' is-taken' : ''}`}
                  onClick={() => patchLine(l.id_detail_livraison, { taken: !l.taken })}>
                  <NdoeImage src={l.img} alt={l.nom_produit} size="lg" preview={false} />
                  <span className="dw-pair__t">
                    <b>{l.nom_produit}</b>
                    {l.reference && <code className="dw-ref">{l.reference}</code>}
                    <small>P{l.taille} · {l.couleur} · Qté {l.quantite}</small>
                  </span>
                  <span className={`dw-check${l.taken ? ' on' : ''}`}>{l.taken ? '✓' : ''}</span>
                </button>
                {!l.taken && (
                  <label className="dw-reason">
                    <span>Motif</span>
                    <select value={l.reason || 'INDISPONIBLE_AU_DEPART'}
                      onChange={(e) => patchLine(l.id_detail_livraison, { reason: e.target.value })}>
                      {REASONS.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
                    </select>
                  </label>
                )}
              </div>
            ))}
          </div>
          <p className="dw-hint">Décoche celles que tu n’as pas pu emporter, et dis pourquoi.</p>
        </div>
        <div className="dw-cta">
          <ErrCta />
          <button type="button" className="dw-btn primary" disabled={saving}
            onClick={() => onTakeCharge()}>{saving ? 'Enregistrement…' : 'Je pars livrer →'}</button>
        </div>
      </main>
    );
  }

  // ── Étape encaissement ─────────────────────────────────────────────────────
  if (etape === 'paiement') {
    return (
      <main className="dw">
        <button type="button" className="dw-back" onClick={() => setEtape('resultat')}>← Résultat</button>
        {Entete}
        <div className="dw-step">
          <WizardStepper steps={steps} current={etape} />
          <h3 className="dw-q">{deliveredLines.length > 0 ? `Combien ${order.client?.nom || 'la cliente'} a-t-elle payé ?` : 'Acompte encaissé (optionnel)'}</h3>
          {deliveredLines.length > 0 && (
            <>
              {acompteCouvertVente > 0 && (
                <p className="dw-attendu">Déjà versé (acompte) : <b>{money(acompteCouvertVente)}</b></p>
              )}
              <p className="dw-attendu">{deliveredLines.length} paire(s) gardée(s) · à encaisser : <b>{money(resteAEncaisser)}</b></p>
            </>
          )}
          <div className="dw-amount">
            <span className="dw-amount__lbl">Montant reçu</span>
            <div className="dw-amount__field">
              <input inputMode="numeric" value={payment} placeholder="0"
                onChange={(e) => changePayment(e.target.value)} />
              <span>$</span>
            </div>
            <span className="dw-amount__cap">max {money(paymentCap)}</span>
          </div>
          {deliveredLines.length > 0 && (
            <div className="dw-quick" role="group" aria-label="Encaissement rapide">
              {/* Raccourcis (le champ reste éditable) — changePayment borne la valeur. */}
              <button type="button" onClick={() => changePayment(String(resteAEncaisser))}>Payé en entier</button>
              <button type="button" className="ghost" onClick={() => changePayment('0')}>À crédit (0)</button>
            </div>
          )}
          {balanceDue > 0 && <p className="dw-attendu is-du">Reste à crédit : <b>{money(balanceDue)}</b> (créance).</p>}
          {acompteEssai > 0 && <p className="dw-attendu">Dont acompte à l’essai : <b>{money(acompteEssai)}</b>.</p>}
          {deliveredLines.length > 0 && String(payment).trim() === '' && (
            <p className="dw-hint" style={{ textAlign: 'center' }}>Saisis le montant reçu — mets <b>0</b> s’il s’agit d’une vente à crédit.</p>
          )}
        </div>
        <div className="dw-cta">
          <ErrCta />
          <button type="button" className="dw-btn primary"
            disabled={deliveredLines.length > 0 && String(payment).trim() === ''}
            onClick={() => setEtape('recap')}>Continuer →</button>
        </div>
      </main>
    );
  }

  // ── Étape récap ────────────────────────────────────────────────────────────
  if (etape === 'recap') {
    return (
      <main className="dw">
        <button type="button" className="dw-back" onClick={() => setEtape(aEncaisser ? 'paiement' : 'resultat')}>← Retour</button>
        {Entete}
        <div className="dw-step">
          <WizardStepper steps={steps} current={etape} />
          <h3 className="dw-q">On récapitule avant de valider</h3>
          <div className="dw-recap">
            {decidees.map((l) => {
              const o = OUTCOME_BTN.find((x) => x.value === l.outcome);
              return (
                <div className="dw-recap__line" key={l.id_detail_livraison}>
                  <span className={`dw-recap__dot tone-${o?.tone || 'muted'}`} />
                  <NdoeImage src={l.img} alt={l.nom_produit} size="sm" preview={false} className="dw-recap__img" />
                  <span className="dw-recap__t">
                    {l.nom_produit} · P{l.taille}
                    {l.reference && <code className="dw-ref">{l.reference}</code>}
                  </span>
                  <b className="dw-recap__b">{o ? o.titre : '—'}</b>
                </div>
              );
            })}
            {aEncaisser && (
              <div className="dw-recap__tot">💵 Montant reçu <b>{money(Math.max(0, Math.min(Number(payment) || 0, paymentCap)))}</b></div>
            )}
          </div>
          {nonDecidees.length > 0 && (
            <p className="dw-pending" role="note">
              {nonDecidees.length} paire(s) laissée(s) sans réponse resteront en circulation : {nonDecidees.map((l) => `${l.nom_produit} P${l.taille}`).join(', ')}.
            </p>
          )}

          {/* Détails facultatifs : antidatage + note (repliés par défaut pour
              garder le flux simple, mais la capacité reste disponible). */}
          <button type="button" className="dw-detoggle" onClick={() => setShowDetails((v) => !v)}>
            {showDetails ? '▾' : '▸'} Détails (date, note){comment ? ' · note ajoutée' : ''}
          </button>
          {showDetails && (
            <div className="dw-details">
              <label htmlFor="dw-date">Date et heure réelles</label>
              <input id="dw-date" type="datetime-local" value={effectiveAt}
                min={minEffectiveAt ? `${String(minEffectiveAt).slice(0, 10)}T00:00` : undefined}
                max={localDateTime()}
                onChange={(e) => setEffectiveAt(e.target.value)} />
              <small>Saisis une date passée si la livraison a eu lieu plus tôt.</small>
              <label htmlFor="dw-note">Note (facultative)</label>
              <textarea id="dw-note" value={comment} placeholder="Ex. remis au gardien…"
                onChange={(e) => setComment(e.target.value)} />
            </div>
          )}

          <p className="dw-hint">Une fois validé, stock et caisse sont mis à jour. Tu peux encore revenir en arrière.</p>
        </div>
        <div className="dw-cta">
          <ErrCta />
          <button type="button" className="dw-btn primary" disabled={saving || !auMoinsUne}
            onClick={() => onSubmitResult()}>{saving ? 'Validation…' : '✓ Valider la livraison'}</button>
          <button type="button" className="dw-btn ghost" onClick={() => { setEtape('resultat'); setIdx(0); }}>← Corriger</button>
        </div>
      </main>
    );
  }

  // ── Étape résultat ─────────────────────────────────────────────────────────
  // Adaptatif : une paire à la fois dès 2 paires, écran unique si une seule.
  const courante = multi ? activeLines[Math.min(idx, activeLines.length - 1)] : activeLines[0];
  const dernierePaire = !multi || idx >= activeLines.length - 1;
  const avancer = () => {
    if (multi && idx < activeLines.length - 1) { setIdx(idx + 1); return; }
    setEtape(aEncaisser ? 'paiement' : 'recap');
  };
  // Raccourci modèle essai : les paires non encore décidées repartent au magasin.
  const resteRendu = () => activeLines
    .filter((l) => !l.outcome)
    .forEach((l) => patchLine(l.id_detail_livraison, { outcome: 'RETOUR_LIVRAISON' }));

  // ── VUE LISTE (dès 2 paires) : marquer toutes les paires sur un seul écran ──
  if (multi && vueListe) {
    return (
      <main className="dw">
        <button type="button" className="dw-back" onClick={onClose}>← Mes livraisons</button>
        {Entete}
        <div className="dw-step">
          <WizardStepper steps={steps} current={etape} />
          <div className="dw-reshead">
            <h3 className="dw-q">Qu’a gardé {order.client?.nom || 'la cliente'}&nbsp;?</h3>
            <button type="button" className="dw-vuelink" onClick={() => setVueListe(false)}>Une par une</button>
          </div>
          <div className="dw-bulk dw-bulk--reste">
            <button type="button" onClick={resteRendu}>↩︎ Le reste = rendu</button>
          </div>
          <div className="dw-blist">
            {activeLines.map((l) => (
              <div key={l.id_detail_livraison} className={`dw-brow${l.outcome ? ' decided' : ''}`}>
                <div className="dw-brow__id">
                  <NdoeImage src={l.img} alt={l.nom_produit} size="lg" preview={false} />
                  <span className="dw-brow__t">
                    <b>{l.nom_produit}</b>
                    {l.reference && <code className="dw-ref">{l.reference}</code>}
                    <small>P{l.taille} · {l.couleur}</small>
                  </span>
                </div>
                <div className="dw-brow__opts">
                  {OUTCOME_BTN.map((o) => (
                    <button type="button" key={o.value}
                      className={`dw-chip ${o.tone}${l.outcome === o.value ? ' sel' : ''}`}
                      onClick={() => patchLine(l.id_detail_livraison, { outcome: o.value })}>
                      <span className="dw-chip__ic" aria-hidden="true">{o.ic}</span>
                      <span className="dw-chip__t">{o.titre}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <p className="dw-hint">Marque ce qu’elle garde, puis « Le reste = rendu ». Les paires non décidées restent à traiter plus tard.</p>
        </div>
        <div className="dw-cta">
          <ErrCta />
          <button type="button" className="dw-btn primary" disabled={!auMoinsUne}
            onClick={() => setEtape(aEncaisser ? 'paiement' : 'recap')}>
            Continuer ({decidees.length}/{activeLines.length}) →
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="dw">
      <button type="button" className="dw-back" onClick={onClose}>← Mes livraisons</button>
      {Entete}
      <div className="dw-step">
        <WizardStepper steps={steps} current={etape} />
        {multi && (
          <div className="dw-reshead">
            <p className="dw-cap">Paire {Math.min(idx, activeLines.length - 1) + 1} sur {activeLines.length}</p>
            <button type="button" className="dw-vuelink" onClick={() => setVueListe(true)}>Vue liste</button>
          </div>
        )}
        {!courante ? (
          <p className="dw-hint">Aucune paire à traiter pour l’instant.</p>
        ) : (
          <>
            <NdoeImage src={courante.img} alt={courante.nom_produit} size="hero" preview={false} className="dw-hero" />
            <div className="dw-pname-c">{courante.nom_produit} · P{courante.taille}{courante.couleur ? ` · ${courante.couleur}` : ''}</div>
            {courante.reference && <div className="dw-ref-c"><code className="dw-ref">{courante.reference}</code></div>}
            <p className="dw-q center">Qu’a fait {order.client?.nom || 'la cliente'}&nbsp;?</p>
            <div className="dw-choices">
              {OUTCOME_BTN.map((o) => (
                <button type="button" key={o.value}
                  className={`dw-choice ${o.tone}${courante.outcome === o.value ? ' sel' : ''}`}
                  onClick={() => patchLine(courante.id_detail_livraison, { outcome: o.value })}>
                  <span className="dw-choice__ic">{o.ic}</span>
                  <span className="dw-choice__t">{o.titre}<small>{o.sous}</small></span>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
      <div className="dw-cta">
        <ErrCta />
        <button type="button" className="dw-btn primary" disabled={!courante || !courante.outcome} onClick={avancer}>
          {multi && !dernierePaire ? 'Paire suivante →' : 'Continuer →'}
        </button>
        {/* Résultat partiel : sauter une paire (elle reste en circulation). */}
        {multi && (
          <button type="button" className="dw-btn ghost" onClick={avancer}>
            {dernierePaire ? 'Passer cette paire (décider plus tard) →' : 'Décider plus tard →'}
          </button>
        )}
        {multi && idx > 0 && (
          <button type="button" className="dw-btn ghost" onClick={() => setIdx(idx - 1)}>← Paire précédente</button>
        )}
      </div>
    </main>
  );
}
